import { useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Popover,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Controller, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import TuneIcon from '@mui/icons-material/Tune';
import EditIcon from '@mui/icons-material/Edit';
import { type TDashboardFilters, useDashboardContext } from 'screens/dashboard/Dashboard';
import { DynamicFieldWrapper } from 'components/DynamicFormField/components/DynamicFieldWrapper';
import Label from 'components/Typography/Label';
import type {
  DynamicFieldType,
  FieldOptions,
} from 'components/DynamicFormField/DynamicFormField';
import { defaultDateRangeFilter } from '../shared/constants';
import { getIsDomainField } from 'components/DynamicFormField/utility/functions';
import { getComputedMaxDate, isFilterChanged } from '../shared/utils';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { DASHBOARD_DATE_FILTER_FORMAT } from 'screens/dashboard/constants';
import { useIsCustomDashboard } from '../hooks/useIsCustomDashboard';

interface QuickDateRangeProps {
  dateRange: [string | null | undefined, string | null | undefined];
  onMonthClick: () => void;
  onWeekClick: () => void;
  onCustomClick: (event: React.MouseEvent<HTMLElement>) => void;
  isMobile?: boolean;
  isLoading?: boolean;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

export const QuickDateRange = ({
  buttonRef,
  dateRange = [null, null],
  onMonthClick,
  onWeekClick,
  onCustomClick,
  isMobile = false,
  isLoading = false,
}: QuickDateRangeProps) => {
  const { t } = useTranslation();

  // Check if the floored date range values match the button ranges
  // to highlight the correct button. Compare simplified month-day-year
  // strings so we ignore time-of-day.
  const [isWeekRange, setIsWeekRange] = useState(false);
  const [isMonthRange, setIsMonthRange] = useState(false);

  useEffect(() => {
    const [startDate = '', endDate = ''] = dateRange ?? [undefined, undefined];
    const startFmt = startDate
      ? dayjs(startDate).format(DASHBOARD_DATE_FILTER_FORMAT)
      : null;
    const endFmt = endDate ? dayjs(endDate).format(DASHBOARD_DATE_FILTER_FORMAT) : null;

    setIsWeekRange(
      startFmt === dayjs().subtract(8, 'day').format(DASHBOARD_DATE_FILTER_FORMAT) &&
        endFmt === dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT),
    );

    setIsMonthRange(
      startFmt === dayjs().subtract(31, 'day').format(DASHBOARD_DATE_FILTER_FORMAT) &&
        endFmt === dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT),
    );
  }, [dateRange]);

  if (!dateRange) {
    return null;
  }

  return (
    <Box
      data-testid="dashboard-controls-quick-date-range"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'end',
        gap: 1,
        height: 32,
        '> .MuiButton-text': {
          px: '16px', // Match the contained button padding to prevent elements from shifting.
        },
      }}
    >
      <Button
        data-testid="dashboard-controls-month-button"
        onClick={onMonthClick}
        variant={isMonthRange ? 'contained' : 'text'}
        disableElevation
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
      >
        {t('blox.general.date.range.month')}
      </Button>
      <Button
        data-testid="dashboard-controls-week-button"
        onClick={onWeekClick}
        variant={isWeekRange ? 'contained' : 'text'}
        disableElevation
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
      >
        {t('blox.general.date.range.week')}
      </Button>

      {!isMobile && (
        <Button
          data-testid="dashboard-controls-custom-button-desktop"
          ref={buttonRef}
          onClick={onCustomClick}
          variant={!(isWeekRange || isMonthRange) ? 'contained' : 'text'}
          disableElevation
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {t('blox.general.date.range.custom')}
        </Button>
      )}
    </Box>
  );
};

export const DashboardControls = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeSite } = useCosmos();
  const notify = useNotify();
  const theme = useTheme();
  const isMdViewport = useMediaQuery(theme.breakpoints.down('md'));
  const isCustomDashboard = useIsCustomDashboard();
  const {
    dashboard,
    filters,
    setDashboardFilters,
    isLoading,
    initDashboardFiltersRef,
    isEditable,
  } = useDashboardContext();

  const [allFiltersAnchorEl, setAllFiltersAnchorEl] = useState<null | HTMLElement>(null);
  const [customDateRangeAnchorEl, setCustomDateRangeAnchorEl] =
    useState<null | HTMLElement>(null);
  // custom date range anchor not used in simplified controls
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  // Ref for the filter button
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const customDateRangeButtonRef = useRef<HTMLButtonElement>(null);

  const handleOpenAllFilters = (event: React.MouseEvent<HTMLElement>) => {
    setAllFiltersAnchorEl(event.currentTarget);
  };

  // Update the dashboard filters state when the form is submitted.
  const onSubmit: SubmitHandler<Record<string, any>> = (data) => {
    const newFilters = Object.assign({}, data);

    // Validate date range.
    if (newFilters.dateRangeFilter) {
      const [start, end] = newFilters.dateRangeFilter;
      const djsStart = dayjs(start);
      const djsEnd = dayjs(end);

      if (!djsStart.isValid() || !djsEnd.isValid() || djsEnd.isBefore(djsStart)) {
        console.error('End date must be after start date');
        notify(t('blox.dashboards.controls.error'), { type: 'error' });
        return;
      }
    }

    // Set the filters in the dashboard context.
    setDashboardFilters?.((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));

    notify(t('blox.dashboards.controls.success'), {
      type: 'success',
    });

    setAllFiltersAnchorEl(null);
    setCustomDateRangeAnchorEl(null);
  };

  const onError: SubmitErrorHandler<Record<string, any>> = (errors) => {
    notify(t('blox.actions.save_error'), { type: 'error' });
  };

  // RHF init.
  const { control, handleSubmit, setValue, reset } = useForm<
    Partial<NonNullable<TDashboardFilters>>
  >({
    defaultValues: filters ?? {},
  });

  // Guarantee form initializes with the correct values.
  // Reset form when filters change to ensure form stays in sync with context.
  useEffect(() => {
    const updatedValues = {
      ...filters,
      // Handle dateRangeFilter if needed
      ...(filters?.startDate &&
        filters?.endDate && {
          dateRangeFilter: [filters.startDate, filters.endDate],
        }),
    };
    reset(updatedValues);
  }, [filters, reset]);

  // Badge count for popover icon button.
  const badgeCount = dashboard?.filters?.reduce((count, field) => {
    const formValue = control._formValues[field.name];
    if (isFilterChanged({ formValue: formValue, field: field })) {
      count += 1;
    }
    return count;
  }, 0);

  // When selecting custom, open the filters modal for the DateRangePicker dynamic field.
  const handleCustomDateRangeOnClick = (event: React.MouseEvent<HTMLElement>) => {
    if (customDateRangeButtonRef.current) {
      setCustomDateRangeAnchorEl(customDateRangeButtonRef.current);
    }
  };

  const handleWeekDateRangeOnClick = () => {
    const startDate = dayjs().subtract(8, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);
    const endDate = dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);

    // Update the local form.
    setValue('dateRangeFilter', [startDate, endDate]);

    // Trigger form submission to update dashboard context.
    handleSubmit(onSubmit, onError)();
  };

  // Extract the dateRangeFilter field from the dashboard filters.
  const dateRangeField = (dashboard?.filters?.find((f) => f.name === 'dateRangeFilter') ||
    defaultDateRangeFilter) as DynamicFieldType;

  const handleMonthDateRangeOnClick = () => {
    const startDate = dayjs().subtract(31, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);
    const endDate = dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT);

    const fieldName = dateRangeField?.name ?? 'dateRangeFilter';

    // Update the local form only if the dateRangeFilter exists in the dynamic filters.
    // Fall back to the literal name for backward compatibility.
    setValue(fieldName as keyof Partial<NonNullable<TDashboardFilters>>, [
      startDate,
      endDate,
    ]);

    // Trigger form submission to update dashboard context.
    handleSubmit(onSubmit, onError)();
  };

  const handleCloseAllFilters = () => {
    setAllFiltersAnchorEl(null);
    // Reset form to initial filter values
    reset(filters ?? initDashboardFiltersRef?.current ?? {});
  };

  const handleCloseCustomDateRange = () => {
    setCustomDateRangeAnchorEl(null);
    // Reset form to initial filter values
    reset(filters ?? initDashboardFiltersRef?.current ?? {});
  };

  return (
    <Box
      data-testid="dashboard-controls"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        gap: 1,
        position: 'relative',
        opacity: isLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      {/* Quick date actions for the main control bar (desktop). */}
      {!isMdViewport && (
        <QuickDateRange
          buttonRef={customDateRangeButtonRef}
          dateRange={
            control._formValues[
              (dateRangeField?.name ||
                'dateRangeFilter') as keyof NonNullable<TDashboardFilters>
            ] || undefined
          }
          onMonthClick={handleMonthDateRangeOnClick}
          onWeekClick={handleWeekDateRangeOnClick}
          onCustomClick={handleCustomDateRangeOnClick}
          isLoading={isLoading}
        />
      )}

      <Divider
        orientation="vertical"
        sx={{ height: '24px', display: { xs: 'none', md: 'block' } }}
      />

      {/* Filter Form Popover Icon Button */}
      <Tooltip
        title={t('blox.dashboards.controls.filters_tip')}
        placement="bottom-start"
      >
        <span>
          <IconButton
            ref={filterButtonRef}
            data-testid="dashboard-controls-filter-button"
            onClick={handleOpenAllFilters}
            disabled={isLoading || !dashboard?.filters?.length}
          >
            <Badge
              badgeContent={badgeCount}
              color="primary"
            >
              {isLoading ? <CircularProgress size={24} /> : <TuneIcon />}
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      {/* Custom date range filter. */}
      {dateRangeField && (
        <Popover
          data-testid="dashboard-custom-date-range-popover"
          disableEnforceFocus={true}
          open={Boolean(customDateRangeAnchorEl)}
          anchorEl={customDateRangeAnchorEl}
          onClose={handleCloseCustomDateRange}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper
            data-testid="dashboard-custom-date-range-form"
            component="form"
            onSubmit={handleSubmit(onSubmit, onError)}
            sx={{ p: 2, maxWidth: 536, minWidth: 360 }}
          >
            {/* Render the dynamic filter fields (dashboard.filters) */}
            {dateRangeField && (
              <Box
                key={dateRangeField.name}
                sx={{
                  mb: 2,
                }}
              >
                <Label>{dateRangeField.label}</Label>
                <Controller
                  name={dateRangeField.name as keyof NonNullable<TDashboardFilters>}
                  control={control}
                  render={({ field: controllerField }) => {
                    // Do not mutate original config; create a local copy with maxDate.
                    const fieldWithMaxDate: DynamicFieldType = {
                      ...dateRangeField,
                      config: {
                        ...(dateRangeField.config ?? {}),
                        maxDate: getComputedMaxDate(dateRangeField.config?.maxDate),
                      },
                    };

                    return (
                      <DynamicFieldWrapper
                        child={fieldWithMaxDate}
                        value={controllerField?.value}
                        onChange={(name, value) => {
                          controllerField.onChange(value);
                        }}
                        isDisabled={false}
                        onValidationChange={(fieldName, isValid) => {
                          setInvalidFields((prev) => {
                            const updated = new Set(prev);
                            isValid ? updated.delete(fieldName) : updated.add(fieldName);
                            return updated;
                          });
                        }}
                      />
                    );
                  }}
                />
              </Box>
            )}
            <Box
              data-testid="dashboard-filters-actions"
              sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
            >
              <Button
                data-testid="dashboard-controls-date-range-cancel-button"
                onClick={handleCloseCustomDateRange}
                disableElevation
              >
                {t('blox.actions.cancel')}
              </Button>

              <Button
                variant="contained"
                data-testid="dashboard-controls-save-filters-button"
                type="submit"
                disabled={invalidFields.size > 0}
                disableElevation
              >
                {t('blox.actions.save')}
              </Button>
            </Box>
          </Paper>
        </Popover>
      )}

      {/* All filters. */}
      <Popover
        data-testid="dashboard-controls-popover"
        disableEnforceFocus={true}
        open={Boolean(allFiltersAnchorEl)}
        anchorEl={allFiltersAnchorEl}
        onClose={handleCloseAllFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Paper
          data-testid="dashboard-controls-form"
          component="form"
          onSubmit={handleSubmit(onSubmit, onError)}
          sx={{ p: 2, maxWidth: 536 }}
        >
          {isCustomDashboard && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              {t('blox.dashboards.controls.filters_warning')}
            </Alert>
          )}

          {/* Quick date actions for the main control bar (mobile). */}
          {isMdViewport && (
            <QuickDateRange
              buttonRef={customDateRangeButtonRef}
              dateRange={[filters?.startDate, filters?.endDate]}
              onMonthClick={handleMonthDateRangeOnClick}
              onWeekClick={handleWeekDateRangeOnClick}
              onCustomClick={handleCustomDateRangeOnClick}
              isMobile={true} // Hide the custom button on mobile since it's redundant with the filter icon button.
              isLoading={isLoading}
            />
          )}

          {/* Render the dynamic filter fields (dashboard.filters) */}
          {!!dashboard?.filters?.length &&
            dashboard?.filters.map((dynamicFieldConfig: DynamicFieldType) => {
              const isPlatformField = dynamicFieldConfig.name === 'platform';
              const isDomainField = getIsDomainField(dynamicFieldConfig);

              // CMSADMIN-2045 - domain/hostname field special ux case
              const fieldOpts: FieldOptions = {
                denumView: isDomainField ? 'domain' : undefined,
              };

              // Create immutable copy of field config to avoid mutating dashboard data
              let fieldConfig: DynamicFieldType = dynamicFieldConfig;

              // Platform: Disable "CTV App", "Native App" until ready.
              // Note: TNCMS-556107 - ticket was rejected.
              // CTV shows up on (.../editorial/dashboard/video)
              if (isPlatformField) {
                const options = dynamicFieldConfig.config?.options ?? [];
                if (options.length) {
                  fieldConfig = {
                    ...dynamicFieldConfig,
                    config: {
                      ...dynamicFieldConfig.config,
                      hideLabel: true,
                      options: options.map((opt: any) => {
                        const value = (opt?.value ?? '').toString().toLowerCase();
                        const isDisabled = value === 'app' || value === 'ctv';
                        return {
                          ...opt,
                          disabled: isDisabled,
                        };
                      }),
                    },
                  };
                }
              }

              if (isDomainField) {
                fieldConfig = {
                  ...fieldConfig,
                  config: {
                    ...fieldConfig.config,
                    hideLabel: true,
                  },
                };
              }

              return (
                <Box
                  key={fieldConfig.name}
                  sx={{
                    mb: 2,
                  }}
                >
                  <Label>{fieldConfig.label}</Label>
                  <Controller
                    name={fieldConfig.name as keyof NonNullable<TDashboardFilters>}
                    control={control}
                    render={({ field: controllerField }) => {
                      let finalFieldConfig = fieldConfig;

                      // If the field is dateRangeFilter, add maxDate to config
                      if (finalFieldConfig.name === 'dateRangeFilter') {
                        const computedMax = getComputedMaxDate(
                          finalFieldConfig.config?.maxDate,
                        );

                        finalFieldConfig = {
                          ...finalFieldConfig,
                          config: {
                            ...finalFieldConfig.config,
                            maxDate: computedMax,
                          },
                        };
                      }

                      return (
                        <DynamicFieldWrapper
                          child={finalFieldConfig}
                          value={controllerField?.value}
                          onChange={(name, value) => {
                            controllerField.onChange(value);
                          }}
                          isDisabled={false}
                          fieldOptions={fieldOpts}
                          onValidationChange={(fieldName, isValid) => {
                            setInvalidFields((prev) => {
                              const updated = new Set(prev);
                              isValid
                                ? updated.delete(fieldName)
                                : updated.add(fieldName);
                              return updated;
                            });
                          }}
                        />
                      );
                    }}
                  />
                </Box>
              );
            })}
          <Box
            data-testid="dashboard-filters-actions"
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
          >
            <Button
              data-testid="dashboard-controls-cancel-button"
              onClick={handleCloseAllFilters}
              disabled={isLoading}
              disableElevation
            >
              {t('blox.actions.cancel')}
            </Button>

            <Button
              variant="contained"
              data-testid="dashboard-controls-save-filters-button"
              type="submit"
              disabled={invalidFields.size > 0 || isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
              disableElevation
            >
              {isLoading ? t('blox.actions.loading') : t('blox.actions.save')}
            </Button>
          </Box>
        </Paper>
      </Popover>

      {isEditable && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => {
            navigate(`/${activeSite}/dashboards/edit/${dashboard?.id}`);
          }}
        >
          Edit
        </Button>
      )}
    </Box>
  );
};

export default DashboardControls;
